import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useWpwHandovers } from "../hooks";
import {
  WPW_DECISION_LABEL,
  WPW_DECLARED_STATUS_COLOR,
  WPW_DECLARED_STATUS_LABEL,
  type WpwHandover,
  type WpwHandoverEvent,
} from "../types";
import { fmtDateTime } from "../utils";

// One human-readable line per timeline event (mirrors WpwHandoverPanel.eventLine, read-only).
function evLine(ev: WpwHandoverEvent): string {
  switch (ev.kind) {
    case "status_change":
      // the body carries the negotiator's reason (mandatory for problem statuses)
      return ev.body
        ? `Status: ${ev.to_status_display} — ${ev.body}`
        : `Status: ${ev.to_status_display}`;
    case "comment":
      return ev.body;
    case "file_added":
      return `Dodano plik: ${ev.attachment_name ?? ""}`;
    case "file_removed":
      return `Usunięto plik: ${ev.attachment_name ?? ""}`;
    case "confirmation": {
      // a per-file decision names the document; the body carries GK's reason
      const label = ev.decision ? WPW_DECISION_LABEL[ev.decision] : ev.kind_display;
      const what = ev.attachment_name ? `${label}: ${ev.attachment_name}` : label;
      return ev.body ? `${what} — ${ev.body}` : what;
    }
    default:
      return ev.kind_display;
  }
}

export function WpwStatusBadge({ status }: { status: WpwHandover["declared_status"] }) {
  return (
    <span
      className="px-1 py-px uppercase tracking-wide text-[9px] border whitespace-nowrap"
      style={{ color: WPW_DECLARED_STATUS_COLOR[status], borderColor: "currentColor" }}
    >
      {WPW_DECLARED_STATUS_LABEL[status]}
    </span>
  );
}

function ConversationCard({ h }: { h: WpwHandover }) {
  return (
    <div className="border border-[var(--color-border)] p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[var(--color-text-primary)] truncate">{h.owner.full_name}</div>
          <div className="text-[11px] font-mono text-[var(--color-text-muted)]">
            dz. {h.parcel.teryt}
            {h.order?.order_number && (
              <>
                {" · "}
                <Link to={`/orders/${h.order.id}`} className="text-[var(--color-primary)] hover:underline">
                  {h.order.order_number}
                </Link>
              </>
            )}
            {!h.order && (
              <span className="text-amber-700 dark:text-amber-400"> · UWAGA: nie ma w żadnym zamówieniu</span>
            )}
          </div>
        </div>
        <WpwStatusBadge status={h.declared_status} />
      </div>

      {h.scope_note && <p className="text-[11px] font-mono text-[var(--color-text-secondary)]">{h.scope_note}</p>}

      {h.current_confirmation && (
        <div className="text-[11px] font-mono text-[var(--color-text-secondary)]">
          Decyzja GK: <b>{h.current_confirmation.decision_display}</b>
          {h.current_confirmation.note ? ` — ${h.current_confirmation.note}` : ""}
        </div>
      )}

      {h.attachments.filter((a) => a.state === "active").length > 0 && (
        <div className="space-y-0.5">
          <span className="section-label">Pliki</span>
          {h.attachments
            .filter((a) => a.state === "active")
            .map((a) => (
              <a
                key={a.id}
                href={a.url ?? "#"}
                target="_blank"
                rel="noreferrer"
                className="block text-[11px] font-mono text-[var(--color-primary)] hover:underline truncate"
              >
                {a.original_name}
              </a>
            ))}
        </div>
      )}

      <div className="space-y-1">
        <span className="section-label">Historia rozmowy</span>
        {h.timeline.length === 0 ? (
          <p className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak wpisów.</p>
        ) : (
          <ul className="space-y-1">
            {[...h.timeline].reverse().map((ev) => (
              <li key={ev.id} className="text-[11px] font-mono border-l-2 border-[var(--color-border-muted)] pl-2">
                <div className="text-[var(--color-text-primary)]">{evLine(ev)}</div>
                <div className="text-[10px] text-[var(--color-text-muted)]">
                  <span className={ev.author_side === "internal" ? "text-[var(--color-primary)]" : "text-amber-600"}>
                    {ev.author_side === "internal" ? "GK" : "Negocjator"}
                  </span>
                  {" · "}
                  {ev.author_name}
                  {" · "}
                  {fmtDateTime(ev.changed_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Read-only preview of one parcel's / owner's WPW conversation(s) — the negotiator's declared
// status, problem note, files and full "historia rozmowy" pulled straight from the order. Reused
// from the Działki table, the parcel drawer and the map (modal). Full editing stays in the WPW tab.
export function WpwConversationModal({
  title,
  ownerId,
  parcelId,
  onClose,
}: {
  title: string;
  ownerId?: string;
  parcelId?: string;
  onClose: () => void;
}) {
  const params: Record<string, string> = ownerId ? { owner: ownerId } : { parcel: parcelId ?? "" };
  const { data: handovers, isLoading } = useWpwHandovers(params);

  return (
    <div
      data-modal-layer
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col bg-[var(--color-bg-surface)] border border-[var(--color-border-strong)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-2.5">
          <h3 className="font-mono text-sm uppercase tracking-wider text-[var(--color-text-primary)]">{title}</h3>
          <button className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !handovers || handovers.length === 0 ? (
            <p className="text-[11px] font-mono text-[var(--color-text-muted)] py-4 text-center">
              Brak zlecenia WPW dla tej działki / właściciela.
            </p>
          ) : (
            handovers.map((h) => <ConversationCard key={h.id} h={h} />)
          )}
        </div>
      </div>
    </div>
  );
}
